import React, { useState } from 'react';
import { T, useTranslate } from '@tolgee/react';
import {
	SypacButton,
	SypacTabs,
	SypacText,
} from '@sypac/component-library-react';
import { ProductStatusEnum } from '../../../../../interfaces/product.interface';
import { Future } from '../../../../../assets/Future';
import EyeIcon from '../../../../../assets/EyeIcon';
import { RejectWeight } from '../../../../../assets/RejectWeight';
import TableStatus from '../../../../../components/TableStatus/TableStatus';
import { ProductTab } from '../ProductTabs/ProductTabs.interface';
import { TabsButton } from '../../../../../components/TabsButton/TabsButton';
import ProductOverview from './components/ProductOverview';
import { ProductDetailsProps } from './ProductDetails.interface';
import { formatDate } from '../../../../../utils/time.util';
import UserAvatar from '../../../../../components/UserAvatar/UserAvatar';

const ProductDetails: React.FC<ProductDetailsProps> = ({
	// onClose,
	product,
	rejectProduct,
	approveProduct,
	viewOnStore,
	editProduct,
	productLanguage,
}) => {
	const { t } = useTranslate();
	const [currentTab, setCurrentTab] = useState<string>('overview');

	const tabs = [
		{
			label: t('productDetails.overview', 'Overview'),
			count: '0',
			value: 'overview',
		},
		{ label: t('productDetails.files', 'Files'), count: '0', value: 'files' },
		{
			label: t('productDetails.history', 'Order history'),
			count: '0',
			value: 'history',
		},
		{ label: t('productDetails.notes', 'Notes'), count: '0', value: 'notes' },
	];

	return (
		<aside className="fixed min-h-full top-0 right-0 w-[716px] border border-solid border-gray-10 rounded-l-2xl bg-white shadow-order-details z-[100] h-screen overflow-y-auto scroll-smooth box-border">
			<div className="sticky top-0 flex flex-col gap-5 bg-white z-[120] pt-[19px]">
				<div className="flex justify-between px-10">
					{['rejected', 'approved'].includes(product.status!) && (
						<section className="flex gap-4 items-center">
							<UserAvatar
								name={product.verifiedByUser?.name}
								size="md"
								photoUrl={product.verifiedByUser?.avatarUrl}
							/>

							<div className="flex flex-col gap-1 mt-[2px]">
								<SypacText variant="overline-regular-large">
									<p className="text-xs text-gray-40">
										<T keyName={`productDetails.${product.status}By`}>
											{product.status === 'approved'
												? 'Approved by:'
												: 'Rejected by:'}
										</T>
									</p>
								</SypacText>

								<div className="flex flex-row gap-4">
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80">
											{product.verifiedByUser?.name}
										</p>
									</SypacText>
									<div className="flex items-center justify-center px-2 py-[2px] bg-gray-1 border border-solid border-gray-10 rounded-full">
										<SypacText variant="body-regular-medium">
											<p className="text-xs text-gray-40">
												{formatDate(product.updatedAt)}
											</p>
										</SypacText>
									</div>
								</div>
							</div>
						</section>
					)}

					<nav className="flex items-center gap-3 ml-auto">
						<SypacButton variant="secondary" size="small">
							<button
								type="button"
								className="flex justify-center items-center w-[42px] h-[42px] bg-white border border-solid border-gray-10 rounded-10 cursor-pointer transition hover:border-gray-30"
								onClick={viewOnStore}
							>
								<span className="flex scale-[1.3]">
									<EyeIcon color="#A2A2A2" />
								</span>
							</button>
						</SypacButton>
						<SypacButton variant="secondary" size="small">
							<button
								type="button"
								className="flex justify-center items-center w-[42px] h-[42px] bg-white border border-solid border-gray-10 rounded-10 cursor-pointer transition hover:border-gray-30"
								onClick={rejectProduct}
							>
								<span className="flex scale-[1.2]">
									<RejectWeight color="#A2A2A2" />
								</span>
							</button>
						</SypacButton>
					</nav>
				</div>

				<header className="w-full flex flex-col gap-3 px-10 py-5 bg-alabaster border border-solid border-gray-10 rounded-10 box-border">
					<div className="w-full flex justify-between items-start">
						<div
							style={{ backgroundImage: `url(${product.photoUrl})` }}
							className="flex flex-none w-[60px] h-[60px] bg-no-repeat bg-cover rounded-10 border border-solid border-gray-10"
							role="img"
							aria-label={product.name}
						/>
						<TableStatus
							state={product?.status || ProductStatusEnum.PENDING}
							isPartial={false}
							insideOut={true}
						/>
					</div>

					<div className="flex flex-col gap-1">
						<SypacText variant="body-regular-medium">
							<p className="text-xl text-gray-80">
								{product?.translations?.find(
									(locale) => locale.language === productLanguage,
								)?.name || product?.name}
							</p>
						</SypacText>
						<SypacText variant="body-regular-medium">
							<p className="text-sm text-gray-50">
								{product?.type} {product?.size ? `(${product?.size})` : ''}
							</p>
						</SypacText>
					</div>
				</header>

				<nav className="px-10">
					<SypacTabs
						variant="gray"
						className="flex w-full p-[3px] gap-[4px] box-border"
					>
						{tabs?.map(({ label, value, count }: ProductTab) => (
							<TabsButton
								key={value}
								label={label}
								buttonId={value!}
								onClick={() => setCurrentTab(value || label.toLowerCase())}
								isSelected={
									currentTab === value || currentTab === label.toLowerCase()
								}
								notification={+count}
								color="#E8E8E8"
								classNames="w-full [&_p:first-child]:text-sm"
							/>
						))}
					</SypacTabs>
				</nav>
			</div>

			<section className="mt-5 mb-10">
				{currentTab === 'overview' ? (
					<ProductOverview
						product={product}
						approveProduct={approveProduct}
						rejectProduct={rejectProduct}
						editProduct={editProduct}
						productLanguage={productLanguage}
					/>
				) : (
					<div className="flex flex-col justify-center items-center pt-8">
						<Future />
						<SypacText variant="heading-5" className="mt-20">
							<p className="text-gray-90 font-medium">
								<T keyName="productDetails.future">
									Feature is not available at the moment
								</T>
							</p>
						</SypacText>
						<SypacText variant="body-regular-medium" className="mt-6">
							<p className="text-gray-40">
								<T keyName="productDetails.futureDescription">
									Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed
									do eiusmod tempor incididunt
								</T>
							</p>
						</SypacText>
					</div>
				)}
			</section>
		</aside>
	);
};

export default ProductDetails;
